import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState, useMemo } from "react";
import { AppShell } from "@/components/AppShell";
import { listInvestPlans, listInvestments, createInvestment, getWalletState } from "@/utils/lending.functions";
import { inr, inrPrecise, riskColor } from "@/lib/lending-format";
import { toast } from "sonner";
import { TrendingUp, Wallet, Calendar, Award } from "lucide-react";

export const Route = createFileRoute("/_authenticated/invest")({
  head: () => ({ meta: [
    { title: "Invest — LendyTrack" },
    { name: "description", content: "Browse fixed-return investment plans, simulate earnings, and grow your portfolio." },
  ] }),
  component: InvestPage,
});

function InvestPage() {
  const plans = useQuery({ queryKey: ["invest-plans"], queryFn: () => listInvestPlans() });
  const investments = useQuery({ queryKey: ["my-investments"], queryFn: () => listInvestments() });
  const wallet = useQuery({ queryKey: ["wallet"], queryFn: () => getWalletState() });

  const totalInvested = useMemo(
    () => (investments.data ?? []).reduce((s, i) => s + Number(i.amount), 0),
    [investments.data],
  );
  const expectedTotal = useMemo(
    () => (investments.data ?? []).reduce((s, i) => s + Number(i.expected_return), 0),
    [investments.data],
  );

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Investor</div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">Invest & earn fixed returns</h1>
        <p className="mt-1 text-sm text-muted-foreground">Pick a plan, fund it from your wallet, and track monthly earnings.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-3 mb-8">
        <StatCard icon={Wallet} label="Wallet balance" value={inrPrecise(wallet.data?.balance ?? 0)} />
        <StatCard icon={TrendingUp} label="Total invested" value={inr(totalInvested)} />
        <StatCard icon={Award} label="Expected at maturity" value={inr(expectedTotal)} />
      </div>

      <h2 className="font-display text-xl font-semibold mb-4">Available plans</h2>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {(plans.data ?? []).map((p) => (
          <PlanCard key={p.id} plan={p} balance={wallet.data?.balance ?? 0} />
        ))}
      </div>

      <h2 className="font-display text-xl font-semibold mt-10 mb-4">My investments</h2>
      {investments.isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : (investments.data ?? []).length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
          You haven't invested yet. Pick a plan above to start earning.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left">Plan</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-right">Rate</th>
                <th className="px-4 py-3 text-right">Maturity</th>
                <th className="px-4 py-3 text-right">Expected</th>
              </tr>
            </thead>
            <tbody>
              {(investments.data ?? []).map((i: any) => (
                <tr key={i.id} className="border-t border-border">
                  <td className="px-4 py-3 font-medium">{i.plan?.name ?? "—"}</td>
                  <td className="px-4 py-3 text-right">{inr(i.amount)}</td>
                  <td className="px-4 py-3 text-right">{i.annual_rate}%</td>
                  <td className="px-4 py-3 text-right text-muted-foreground">{i.maturity_date}</td>
                  <td className="px-4 py-3 text-right font-semibold text-success">{inr(i.expected_return)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}

function StatCard({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-2 font-display text-2xl font-semibold">{value}</div>
    </div>
  );
}

function PlanCard({ plan, balance }: { plan: any; balance: number }) {
  const router = useRouter();
  const qc = useQueryClient();
  const invest = useServerFn(createInvestment);
  const [amount, setAmount] = useState<string>(String(plan.min_amount));

  const numericAmount = Number(amount) || 0;
  const years = plan.tenure_months / 12;
  const projected = numericAmount * Math.pow(1 + Number(plan.annual_rate) / 100, years);

  const m = useMutation({
    mutationFn: (amt: number) => invest({ data: { planId: plan.id, amount: amt } }),
    onSuccess: () => {
      toast.success(`Invested ${inr(numericAmount)} in ${plan.name}`);
      qc.invalidateQueries({ queryKey: ["my-investments"] });
      qc.invalidateQueries({ queryKey: ["wallet"] });
      router.invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-soft flex flex-col">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-display text-lg font-semibold">{plan.name}</div>
          <div className="text-xs text-muted-foreground">{plan.payout_frequency} payout</div>
        </div>
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${riskColor(plan.risk_grade)}`}>
          Grade {plan.risk_grade}
        </span>
      </div>
      <div className="mt-3 text-xs text-muted-foreground">{plan.description}</div>
      <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
        <div><span className="text-muted-foreground">Rate</span><div className="font-semibold text-success">{plan.annual_rate}% p.a.</div></div>
        <div><span className="text-muted-foreground">Tenure</span><div className="font-semibold">{plan.tenure_months} months</div></div>
      </div>
      <div className="mt-4 space-y-2">
        <label className="text-xs text-muted-foreground">Amount ({inr(plan.min_amount)} – {inr(plan.max_amount)})</label>
        <input
          type="number"
          min={plan.min_amount}
          max={plan.max_amount}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        <div className="text-xs text-muted-foreground">
          Projected at maturity: <span className="font-semibold text-foreground">{inr(projected)}</span>
        </div>
      </div>
      <button
        disabled={m.isPending || numericAmount < Number(plan.min_amount) || numericAmount > balance}
        onClick={() => m.mutate(numericAmount)}
        className="mt-4 rounded-md bg-primary py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
      >
        {m.isPending ? "Investing…" : numericAmount > balance ? "Add funds to wallet" : "Invest now"}
      </button>
    </div>
  );
}
